import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { omit } from "lodash";
import { passing_draw_polygon as passingDrawPolygon } from "mapbox-gl-draw-passing-mode";

import { booleanIntersects, difference, featureCollection } from "fond/turf";
import { MapboxStyleLayer } from "fond/types";
import { isAnyPolygon } from "fond/types/geojson";

import directSelectMode from "./modes/base/directSelect";
import drawLineString from "./modes/base/drawLineString";
import drawPoint from "./modes/base/drawPoint";
import drawPolygon from "./modes/base/drawPolygon";
import polygonCutMode from "./modes/base/polygonCut";
import polygonSelect from "./modes/base/polygonSelect";
import polygonSplitMode from "./modes/base/polygonSplit";
import shortestPathFeature from "./modes/base/shortestPathFeature";
import simpleSelectMode from "./modes/base/simpleSelect";
import snapAddFeature from "./modes/base/snapAddFeature";
import snapDeleteFeature from "./modes/base/snapDeleteFeature";
import streetView from "./modes/base/streetView";

// Default snapping options used for drawing within market lens.
export const snappingOptions: Pick<MapboxDrawOptions, "snap" | "snapOptions" | "guides"> = {
  snap: true,
  snapOptions: {
    snapPx: 15,
    snapToMidPoints: false,
    snapVertexPriorityDistance: 0.0025,
  },
  guides: false,
};

/**
 * Validates that a feature (B) is either contained within, or intersects with with another feature (A).
 * @param featureA The feature.
 * @param featureB The feature being tested to see if it with within FeatureA
 */
export const isWithin = (featureA: Feature<Polygon | MultiPolygon>, featureB: Feature<Polygon | MultiPolygon>): boolean =>
  !difference(featureB, featureA) || booleanIntersects(featureA, featureB);

/**
 * Updates features within a feature collection.
 * Note: that the order of the features within the collection is not maintained.
 * @param collection the FeatureCollection that will be updated
 * @param features the features to be upserted into the collection
 */
export const updateFeatureCollection = (collection: FeatureCollection, features: Feature[]): FeatureCollection => {
  return featureCollection([...collection.features.filter((feature) => !features.some((feat) => feat.id === feature.id)), ...features]);
};

/**
 * Prevents the polygon overlapping any existing features.
 */
export const intersect = (polygon: Feature<Polygon | MultiPolygon>, all: Feature[]): Feature<Polygon | MultiPolygon> => {
  const newFeatures: Feature[] = [];
  let diffPoly: Feature<Polygon | MultiPolygon> = { ...polygon };

  all
    .filter(isAnyPolygon)
    .filter((feature) => feature.id !== polygon.id)
    .forEach((feature) => {
      if (isAnyPolygon(feature)) {
        const diff = difference(diffPoly, feature);
        if (diff) diffPoly = diff;
      }
      newFeatures.push(feature);
    });

  return { ...diffPoly, id: polygon.id };
};

/**
 * Clips a feature so that there is no overlap with any other features
 *
 * @param feature The feature to be clipped
 * @param all All features to be clipped against
 * @returns A clipped feature that has no overlap with any existing features
 */
export const clip = (feature: Feature<Polygon | MultiPolygon>, all: Feature[] = []): Feature<Polygon | MultiPolygon> => {
  return intersect(feature, all);
};

/**
 * Validates that polygon features have a turf "difference" when compared to the existing features.
 * In other words, the feature must not be completely contained by another feature.
 *
 * @param features A collection of features to validate
 * @param all A collection of existing features
 * @returns boolean indicating if all the features have a valid difference
 */
export const validateFeatures = (features: Feature[], all: Feature[] = []): boolean => {
  return features.every((newFeature) => {
    if (!newFeature.geometry) return false;

    if (isAnyPolygon(newFeature)) {
      return all
        .filter(({ id }) => id !== newFeature.id)
        .filter(isAnyPolygon)
        .every((feature) => difference(newFeature, feature));
    }

    return true;
  });
};

// A set of drawing modes that are always included, but can be overridden by modes passed via props.
export const baseDrawControlModes: Record<string, MapboxDraw.DrawCustomMode<any, any>> = {
  ...MapboxDraw.modes,
  draw_polygon: drawPolygon,
  draw_point: drawPoint,
  draw_line_string: drawLineString,
  direct_select: directSelectMode,
  passing_draw_polygon: passingDrawPolygon,
  polygon_select: polygonSelect,
  ...polygonCutMode,
  ...polygonSplitMode,
  simple_select: simpleSelectMode,
  shortest_path_feature: shortestPathFeature,
  snap_add_feature: snapAddFeature,
  snap_delete_feature: snapDeleteFeature,
  street_view: streetView,
  static: StaticMode,
};

/**
 * Combines the base drawing modes with any custom modes, allowing for overrides or disabling of specific modes.
 *
 * For example, to disable a mode, you can set it to null in the customModes parameter.
 *
 * { draw_polygon: null } will disable the draw_polygon mode.
 *
 * @param customModes An optional record of custom drawing modes to include or override.
 * @returns A record of drawing modes to be used by the Mapbox Draw control.
 */
export const getDrawModes = (customModes?: Record<string, MapboxDraw.DrawCustomMode>): Record<string, MapboxDraw.DrawCustomMode> => {
  return omit(
    {
      // Include these modes always
      ...baseDrawControlModes,
      // mode overrides
      ...(customModes || {}),
    },
    // Allow modes to be disabled by specifically setting them to null
    Object.keys(customModes || {}).filter((key) => customModes?.[key] === null)
  );
};

/**
 * Adds a filter condition to an existing filter expression.
 * Combines filters using an "all" expression.
 *
 * @param existingFilter - The existing Mapbox filter expression (or undefined)
 * @param newCondition - The new filter condition to add
 * @returns A combined filter expression
 */
export const addFilterCondition = (
  existingFilter: MapboxStyleLayer["filter"] | undefined,
  newCondition: MapboxStyleLayer["filter"]
): MapboxStyleLayer["filter"] => {
  if (!existingFilter) {
    return ["all", newCondition];
  }

  // If the existing filter is already an "all" expression, append to it
  if (Array.isArray(existingFilter) && existingFilter[0] === "all") {
    return [...existingFilter, newCondition];
  }

  // Otherwise, wrap both in an "all" expression
  return ["all", existingFilter, newCondition];
};

/**
 * Recursively transforms a Mapbox filter expression to add "user_" prefix to property names.
 * This is needed because Mapbox Draw prefixes all feature properties with "user_".
 *
 * @param filter - The Mapbox filter expression to transform
 * @returns Transformed filter with "user_" prefixed property names
 *
 * @example
 * ```typescript
 * // Input: ["==", ["get", "Tier"], "T1"]
 * // Output: ["==", ["get", "user_Tier"], "T1"]
 *
 * // Input: ["all", ["==", ["get", "Tier"], "T1"], ["!=", ["get", "Type"], null]]
 * // Output: ["all", ["==", ["get", "user_Tier"], "T1"], ["!=", ["get", "user_Type"], null]]
 * ```
 */
export const transformFilterForDrawMode = (filter: MapboxStyleLayer["filter"]): MapboxStyleLayer["filter"] => {
  if (!filter) return filter;
  if (!Array.isArray(filter)) return filter;

  // Check if this is a ["get", "propertyName"] expression
  if (filter.length === 2 && filter[0] === "get" && typeof filter[1] === "string") {
    // Don't prefix if it already has the user_ prefix or is a special property
    if (filter[1].startsWith("user_") || filter[1].startsWith("$") || filter[1] === "id") {
      return filter;
    }
    return ["get", `user_${filter[1]}`];
  }

  // Recursively transform nested arrays
  return filter.map((element) => transformFilterForDrawMode(element as MapboxStyleLayer["filter"])) as MapboxStyleLayer["filter"];
};

/**
 * Recursively transforms expressions in paint/layout properties to add "user_" prefix to property names.
 * This is needed because Mapbox Draw prefixes all feature properties with "user_".
 *
 * @param value - The value to transform (can be a primitive, array, or object)
 * @returns Transformed value with "user_" prefixed property names in expressions
 *
 * @example
 * ```typescript
 * // Input: ["get", "Tier1"]
 * // Output: ["get", "user_Tier1"]
 *
 * // Input: { "line-offset": ["get", "Tier1"], "line-width": 2 }
 * // Output: { "line-offset": ["get", "user_Tier1"], "line-width": 2 }
 * ```
 */
export const transformExpressionsForDrawMode = (value: unknown): unknown => {
  if (!value) return value;

  // If it's an array, it might be an expression
  if (Array.isArray(value)) {
    // Check if this is a ["get", "propertyName"] expression
    if (value.length === 2 && value[0] === "get" && typeof value[1] === "string") {
      // Don't prefix if it already has the user_ prefix or is a special property
      if (value[1].startsWith("user_") || value[1].startsWith("$") || value[1] === "id") {
        return value;
      }
      return ["get", `user_${value[1]}`];
    }

    // Recursively transform nested arrays
    return value.map((element) => transformExpressionsForDrawMode(element));
  }

  // If it's an object, recurse through its properties
  if (typeof value === "object" && value !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = transformExpressionsForDrawMode(val);
    }
    return result;
  }

  // Primitive value, return as-is
  return value;
};

/**
 * Transforms layer configuration styles for use with MapboxDraw drawing tools.
 *
 * This function:
 * 1. Prefixes style IDs to avoid conflicts with default drawing styles
 * 2. Transforms paint/layout expressions to use "user_" prefixed property names
 * 3. Adds a filter to only apply to actual features (not drawing UI elements)
 *
 * @param layerId - The layer ID to use as a prefix
 * @param layerStyles - The Mapbox style layers from the layer configuration
 * @returns Transformed style layers ready for use with drawing tools
 */
export const transformLayerConfigStylesForDrawing = (layerId: string, layerStyles: MapboxStyleLayer[]): MapboxStyleLayer[] => {
  return layerStyles.map((style) => ({
    ...style,
    // Prefix the ID to avoid conflicts with default drawing styles
    id: `${layerId}-${style.id}`,
    // Transform paint properties to use user_ prefix in expressions (only if defined)
    ...(style.paint && { paint: transformExpressionsForDrawMode(style.paint) }),
    // Transform layout properties to use user_ prefix in expressions (only if defined)
    ...(style.layout && { layout: transformExpressionsForDrawMode(style.layout) }),
    // Add filter to only apply to actual features, not drawing UI elements
    filter: addFilterCondition(style.filter, ["==", ["get", "user_layerId"], layerId]),
  })) as MapboxStyleLayer[];
};
